import {
    useMemo,
    useRef,
    useCallback,
    forwardRef,
    useImperativeHandle,
} from 'react';
import {FormControl} from '@goproblems/ui';
import {MarkdownContent} from '@components';
import {useUsernameAutocomplete} from '@utils';
import {UsernameSuggestions} from './UsernameSuggestions';
import {UserMinimal} from '@types';

function isLikelyMarkdown(text: string): boolean {
    const markdownPatterns = [
        /^#{1,6} /m, // # Heading
        /\*\*(.*?)\*\*/, // **bold**
        /_(.*?)_/, // _italic_
        /\*(.*?)\*/, // *italic*
        /`{1,3}[^`\n]+`{1,3}/, // `inline code` or ```code block```
        /^```[\s\S]+?```$/, // ```code block``` multiline
        /\[(.*?)\]\((.*?)\)/, // [link](url)
        /^[-*+] /m, // list
        /^\d+\.\s/m, // numbered list
        /^>\s/m, // blockquote
        /!\[(.*?)\]\((.*?)\)/, // ![img](url)
        /\[p#(\d+)\]/i, // [p#123] for problem links
        /@([a-zA-Z0-9_]+)/, // @user
        /\[diagram:sgf=/, // [diagram:sgf=(...)]
        /\[senseis:([^\]]+)\]/, // [senseis:title]
        /\[c#(\d+)\]/, // [c#123] for collection links
        /\[info:([a-z0-9_-]+)\]/, // [info:slug] for info page links
    ];
    return markdownPatterns.some(pattern => pattern.test(text));
}

export type MdEditorHandle = {
    getTextarea: () => HTMLTextAreaElement | null;
};

type MdEditorProps = {
    id?: string;
    value: string;
    onChange: (value: string) => void;
    placeholder?: string;
    rows?: number;
    onKeyDown?: (event: React.KeyboardEvent) => void;
    disabled?: boolean;
    contextUsers?: UserMinimal[];
};

export const MdEditor = forwardRef<MdEditorHandle, MdEditorProps>(
    (
        {
            id,
            value,
            onChange,
            placeholder,
            rows = 3,
            onKeyDown,
            disabled,
            contextUsers,
        },
        ref
    ) => {
        const textareaRef = useRef<HTMLTextAreaElement>(null);
        const containerRef = useRef<HTMLDivElement>(null);

        useImperativeHandle(ref, () => ({
            getTextarea: () => textareaRef.current,
        }));

        const {
            suggestions,
            isLoading,
            showSuggestions,
            selectedIndex,
            onInputChange,
            onKeyDown: autocompleteKeyDown,
            onSuggestionClick,
            setSelectedIndex,
        } = useUsernameAutocomplete(value, onChange, textareaRef, contextUsers);

        const showPreview = useMemo(() => isLikelyMarkdown(value), [value]);

        const handleTextareaChange = useCallback(
            (e: React.ChangeEvent<HTMLTextAreaElement>) => {
                const newValue = e.target.value;
                onChange(newValue);
                onInputChange(newValue, e.target.selectionStart || 0);
            },
            [onChange, onInputChange]
        );

        const handleKeyDown = useCallback(
            (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
                if (showSuggestions && autocompleteKeyDown(e)) {
                    return;
                }

                if (onKeyDown) {
                    onKeyDown(e);
                }
            },
            [showSuggestions, autocompleteKeyDown, onKeyDown]
        );

        const getSuggestionPosition = useCallback(() => {
            if (!textareaRef.current || !containerRef.current)
                return {top: 0, left: 0};

            const textarea = textareaRef.current;
            const container = containerRef.current;

            const containerRect = container.getBoundingClientRect();

            const scrollTop = textarea.scrollTop;
            const lineHeight =
                parseInt(getComputedStyle(textarea).lineHeight) || 20;

            const textBeforeCursor = textarea.value.substring(
                0,
                textarea.selectionStart
            );
            const linesBefore = (textBeforeCursor.match(/\n/g) || []).length;

            const cursorY = linesBefore * lineHeight - scrollTop;
            const absoluteTop =
                containerRect.top + cursorY + lineHeight + 15 + window.scrollY;
            const absoluteLeft = containerRect.left + 10 + window.scrollX;

            return {
                top: absoluteTop,
                left: absoluteLeft,
            };
        }, []);

        return (
            <div ref={containerRef} style={{position: 'relative'}}>
                <FormControl
                    id={id}
                    ref={textareaRef}
                    className="text-xs"
                    as="textarea"
                    rows={rows}
                    placeholder={placeholder}
                    value={value}
                    onChange={handleTextareaChange}
                    onKeyDown={handleKeyDown}
                    disabled={disabled}
                />
                {showSuggestions && (
                    <UsernameSuggestions
                        suggestions={suggestions}
                        selectedIndex={selectedIndex}
                        onSuggestionClick={onSuggestionClick}
                        setSelectedIndex={setSelectedIndex}
                        position={getSuggestionPosition()}
                        isLoading={isLoading}
                    />
                )}
                {showPreview && (
                    <div className="mt-2 text-xs border rounded p-2">
                        <MarkdownContent text={value} />
                    </div>
                )}
            </div>
        );
    }
);

MdEditor.displayName = 'MdEditor';
